import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

export const REVEAL_SELECTOR = "[data-reveal]";
export const REVEAL_GROUP_SELECTOR = "[data-reveal-group]";

const PREFERS_REDUCED_MOTION_MQ = window.matchMedia("(prefers-reduced-motion: reduce)");
const REVEAL_START = "top 72%";
const REVEAL_START_OFFSET = 0.72;
const REVEAL_LAYOUT_EVENTS = ["load", "pageshow", "codista:slider:init"];

gsap.registerPlugin(ScrollTrigger);

export function prefersReducedMotion() {
  return PREFERS_REDUCED_MOTION_MQ.matches;
}

function getRevealDelay(item, fallback = 0) {
  const delay = Number.parseFloat(item.dataset.revealDelay);

  return Number.isFinite(delay) ? delay : fallback;
}

export function revealElements(elements, options = {}) {
  const items = Array.from(elements).filter(Boolean);

  if (!items.length) {
    return;
  }

  gsap.killTweensOf(items);

  if (prefersReducedMotion()) {
    gsap.set(items, { autoAlpha: 1, clearProps: "opacity,visibility,transform" });
    return;
  }

  gsap.fromTo(
    items,
    {
      autoAlpha: 0,
      y: options.y ?? 32,
    },
    {
      autoAlpha: 1,
      y: 0,
      duration: options.duration ?? 0.7,
      ease: options.ease ?? "power3.out",
      delay: (index, item) => getRevealDelay(item, options.delay ?? 0),
      stagger: options.stagger ?? 0.1,
      clearProps: "opacity,visibility,transform",
      onComplete: () => {
        items.forEach((item) => {
          item.dataset.revealed = "true";
        });
      },
    },
  );
}

function revealOnce(elements) {
  const items = Array.from(elements).filter((item) => item.dataset.revealed !== "true");

  if (!items.length) {
    return;
  }

  items.forEach((item) => {
    item.dataset.revealed = "true";
  });

  revealElements(items);
}

function initRevealGroup(group, items) {
  if (group.getBoundingClientRect().top <= window.innerHeight * REVEAL_START_OFFSET) {
    revealOnce(items);
    return;
  }

  ScrollTrigger.create({
    trigger: group,
    start: REVEAL_START,
    once: true,
    onEnter: () => revealOnce(items),
  });
}

function initReveals() {
  const revealItems = document.querySelectorAll(REVEAL_SELECTOR);

  if (!revealItems.length) {
    return;
  }

  if (prefersReducedMotion()) {
    revealOnce(revealItems);
    return;
  }

  gsap.set(revealItems, { autoAlpha: 0, y: 32 });

  const revealGroups = new Map();

  revealItems.forEach((item) => {
    const group =
      item.closest(REVEAL_GROUP_SELECTOR) ||
      item.closest("swiper-container") ||
      item.parentElement ||
      item;
    const items = revealGroups.get(group) || [];

    items.push(item);
    revealGroups.set(group, items);
  });

  revealGroups.forEach((items, group) => initRevealGroup(group, items));
}

initReveals();

function refreshRevealPositions() {
  if (prefersReducedMotion()) {
    return;
  }

  requestAnimationFrame(() => ScrollTrigger.refresh());
}

REVEAL_LAYOUT_EVENTS.forEach((eventName) => {
  window.addEventListener(eventName, refreshRevealPositions);
});
